import { Brand } from '@awaze/design-system.components.awaze-theme-provider'
import getConfig from 'next/config'
import { PlacesSearchConfigType } from 'src/components/SearchForm/SearchForm.types'
import {
  AGE_SEARCH_MERCH_BANNER_VARIANTS,
  OPTIMIZELY_FLAGS_ENUM,
} from 'src/constants'
import { useOptimizelyDecisionsContext } from 'src/context/optimizely-decisions-context'

interface MerchBannerExp {
  shouldShowMerchBannerExp: boolean
  merchBannerVariables:
    | {
        [variableKey: string]: unknown
      }
    | undefined
}

export const useMerchBannerExperimentValues = (): MerchBannerExp => {
  const optiContext = useOptimizelyDecisionsContext()

  const shouldShowMerchBannerExp =
    optiContext?.isExperimentEnabled(
      OPTIMIZELY_FLAGS_ENUM.AGE_SEARCH_MERCH_BANNER,
    ) &&
    optiContext?.isExperimentVariationKeyOneOf(
      OPTIMIZELY_FLAGS_ENUM.AGE_SEARCH_MERCH_BANNER,
      AGE_SEARCH_MERCH_BANNER_VARIANTS,
    )

  const merchBannerVariables = optiContext?.getExperimentVariables(
    OPTIMIZELY_FLAGS_ENUM.AGE_SEARCH_MERCH_BANNER,
  )

  return {
    shouldShowMerchBannerExp,
    merchBannerVariables,
  }
}

export const useOptimizelyPlacesJson = (
  brand: Brand,
  locale: string | undefined,
  placesSearchConfig: PlacesSearchConfigType,
): PlacesSearchConfigType => {
  const { publicRuntimeConfig } = getConfig()
  const { APP_ENV } = publicRuntimeConfig
  const optimizelyVariableKey =
    publicRuntimeConfig[APP_ENV]?.features?.placesApiOptimizelyVariableKey

  const optiContext = useOptimizelyDecisionsContext()
  const optimizelyPlacesData = optiContext?.getExperimentVariables(
    OPTIMIZELY_FLAGS_ENUM.AGE_MFE_ACTIVATE_PLACES_API,
  )?.[optimizelyVariableKey]

  // Is Places-XP- & Enable-On-Search-MFE -flag enabled in Optimizely
  if (
    optiContext?.isExperimentEnabled(
      OPTIMIZELY_FLAGS_ENUM.AGE_MFE_ACTIVATE_PLACES_API,
    ) &&
    optimizelyPlacesData?.['enableOnSearchMfe']
  ) {
    placesSearchConfig.useLegacyDestinationSelector =
      !!optimizelyPlacesData?.[brand]?.useLegacyDestinationSelector

    placesSearchConfig.initialParentDestinationList =
      optimizelyPlacesData?.[brand]?.initialParentDestinationList
        ?.filter((optimizelyObject) => optimizelyObject.locale === locale)
        .map((initParent) => initParent.placesApiId)
        .join() || null

    placesSearchConfig.googleFallbackMethod =
      optimizelyPlacesData?.[brand]?.googleFallbackMethod?.[locale] ||
      placesSearchConfig.googleFallbackMethod

    placesSearchConfig.pageSize = optimizelyPlacesData?.['pageSize']
    placesSearchConfig.pageSizePopular =
      optimizelyPlacesData?.['pageSizePopular']
  }

  return placesSearchConfig
}
